define('webshopProductsCategoriesView',[
  'app',
  'module',
  'backbone',
  'marionette',
  'underscore',
  'buttonView',
  'modalsConfirmationView',
  'modalsAlertView',
  'webshopCategoriesFinderView',
  'webshopProductsCategoriesClipboardView',
  'webshopProductsCategoriesListView',
  'entities/webshop/categories',
  'template!webshopProductsCategoriesView'
], function (
  App,
  Module,
  Backbone,
  Marionette,
  _,
  ButtonView,
  ConfirmationView,
  AlertView,
  CategoriesFinderView,
  ClipboardView,
  ListView,
  Categories,
  Tpl
) {
  'use strict';

  /**
   * Products x categories grid.
   *
   * Each product row shows one cell per category it belongs to. Clicking a
   * filled cell copies its category (the "clipboard" shown in the header),
   * an empty cell can receive a new category (finder) or the copied one.
   * The header buttons paste / remove the copied category on every selected row.
   */
  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'webshop-products-categories-view',

    ui: {
      clipboardEmpty: '.header .clipboard-empty',
      selectionInfos: '.header .selection-infos'
    },

    regions: {
      clipboardRegion: '.header .clipboard-region',
      chooseRegion: '.header .choose-region',
      pasteRegion: '.header .paste-region',
      removeRegion: '.header .remove-region',
      listRegion: '.list-region'
    },

    initialize: function () {
      // the copied category: {id, code, name}, id === '' when empty
      this.clipboard = new Backbone.Model({id: '', code: '', name: ''});
      this.selectedProducts = new Backbone.Model({products: []});
      this.running = false;
    },

    serializeData: function () {
      return {
        clipboardLabel: _.i18n('webshop.products.categories.clipboard', null, 'Copied category'),
        clipboardEmpty: _.i18n('webshop.products.categories.clipboard.empty', null,
          'none - click a category in the list, or choose one')
      };
    },

    onShow: function () {
      this.clipboardView = new ClipboardView({
        model: this.clipboard
      });
      this.getRegion('clipboardRegion').show(this.clipboardView);

      //----------------
      this.chooseButtonView = new ButtonView({
        type: 'find',
        color: 'lightgrey',
        text: _.i18n('webshop.products.categories.clipboard.choose', null, 'Choose a category'),
        title: _.i18n('webshop.products.categories.clipboard.choose.title', null, 'Choose the category to paste')
      });
      this.getRegion('chooseRegion').show(this.chooseButtonView);
      this.chooseButtonView.on('click', _.bind(this.onChoose, this));

      //----------------
      this.pasteButtonView = new ButtonView({
        type: 'add',
        color: 'orange',
        text: _.i18n('webshop.products.categories.paste', null, 'Paste on selection'),
        title: _.i18n('webshop.products.categories.paste.title', null, 'Add the copied category to the selected products')
      });
      this.getRegion('pasteRegion').show(this.pasteButtonView);
      this.pasteButtonView.on('click', _.bind(this.onPaste, this));

      //----------------
      this.removeButtonView = new ButtonView({
        type: 'remove',
        color: 'grey',
        text: _.i18n('webshop.products.categories.remove', null, 'Remove from selection'),
        title: _.i18n('webshop.products.categories.remove.title', null, 'Remove the copied category from the selected products')
      });
      this.getRegion('removeRegion').show(this.removeButtonView);
      this.removeButtonView.on('click', _.bind(this.onRemove, this));

      //----------------
      this.listView = new ListView({
        selectedProducts: this.selectedProducts,
        clipboard: this.clipboard
      });
      this.getRegion('listRegion').show(this.listView);

      //----------------
      this.listenTo(this.clipboard, 'change', this.renderState);
      this.listenTo(this.selectedProducts, 'change', this.renderState);
      this.renderState();
    },

    renderState: function () {
      if (this.isDestroyed)
        return;

      var products = this.selectedProducts.get('products');
      var hasClipboard = this.clipboard.get('id') !== '';

      if (hasClipboard)
        this.ui.clipboardEmpty.hide();
      else
        this.ui.clipboardEmpty.css('display', 'flex');

      this.ui.selectionInfos.html(
        _.i18n('webshop.products.categories.selection', null, '%s product(s) selected').replace('%s', products.length));

      var enabled = !this.running && products.length > 0 && hasClipboard;
      this.pasteButtonView.enabled(enabled);
      this.removeButtonView.enabled(enabled);
      this.chooseButtonView.enabled(!this.running);
    },

    onChoose: function () {
      CategoriesFinderView.prototype.open({
        title: _.i18n('webshop.products.categories.clipboard.finder.title', null, 'Choose the category to paste'),
        onSelected: _.bind(function (categories) {
          if (categories.length > 0) {
            this.clipboard.set({
              id: categories[0].get('id'),
              code: categories[0].get('code'),
              name: categories[0].getName ? categories[0].getName() : categories[0].get('code')
            });
          }
        }, this)
      });
    },

    onPaste: function () {
      this.run('add');
    },

    onRemove: function () {
      var products = this.selectedProducts.get('products');

      ConfirmationView.prototype.open({
        title: _.i18n('webshop.products.categories.remove.confirmation.title', null, 'Remove from category'),
        text: _.i18n('webshop.products.categories.remove.confirmation.text', null,
          'Remove %s product(s) from the category "%c"?')
          .replace('%s', products.length)
          .replace('%c', this.clipboard.get('code')),
        onConfirm: _.bind(function () {
          this.run('remove');
        }, this)
      });
    },

    /**
     * Adds / removes the copied category on every selected product.
     */
    run: function (action) {
      if (this.running)
        return;

      var productIds = _.map(this.selectedProducts.get('products'), function (product) {
        return product.getId();
      });
      var categoryId = this.clipboard.get('id');

      if (productIds.length === 0 || categoryId === '')
        return;

      this.running = true;
      this.renderState();
      var button = action === 'add' ? this.pasteButtonView : this.removeButtonView;
      button.loading(true);

      var request;
      if (action === 'add')
        request = App.request('webshop.categories.products.add', categoryId, productIds);
      else
        request = App.request('webshop.categories.products.delete', categoryId, productIds);

      request.done(_.bind(function () {
        this.onRunDone(button);
      }, this)).fail(_.bind(function (message) {
        this.onRunDone(button);
        AlertView.prototype.open(
          _.i18n('webshop.products.categories.error.title', null, 'Error'),
          message || _.i18n('webshop.products.categories.error.text', null, 'The update failed'));
      }, this));
    },

    onRunDone: function (button) {
      this.running = false;

      if (this.isDestroyed)
        return;

      button.loading(false);
      this.renderState();
      this.listView.refreshCategories();
    }
  });
});

